// src/features/MediaLibrary/MediaBrowser.js - With Pagination
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { 
  collection, 
  query, 
  orderBy, 
  limit, 
  startAfter, 
  getDocs,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import MediaService from './MediaService';
import './MediaBrowser.css';

const MediaBrowser = ({ 
  onSelectMedia, 
  selectedMediaId, 
  showSearchBar = true, 
  showCategoryFilter = true 
}) => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [message, setMessage] = useState({ type: '', text: '' });
  const [isDeleting, setIsDeleting] = useState(null);
  const [showPreview, setShowPreview] = useState(null);
  
  // PAGINATION STATE
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [lastVisible, setLastVisible] = useState(null);
  const [totalCount, setTotalCount] = useState(0);
  const ITEMS_PER_PAGE = 10;

  const categories = ['story', 'lesson', 'activity', 'reference', 'exercise', 'background', 'illustration', 'diagram', 'character'];

  // OPTIMIZED: Memoize filtered media for search
  const filteredMedia = useMemo(() => {
    if (!searchTerm && !selectedCategory) return media;
    
    return media.filter(item => {
      const matchesSearch = !searchTerm || 
        item.filename.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.tags && item.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))) ||
        (item.description && item.description.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesCategory = !selectedCategory || item.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [media, searchTerm, selectedCategory]);

  // IMPROVED: Paginated fetch function
  const fetchMedia = useCallback(async (page = 1, isRefresh = false) => {
    try {
      setLoading(true);
      setError(null);
      
      let mediaQuery = query(
        collection(db, 'imageAssets'),
        orderBy('uploadedAt', 'desc'),
        limit(ITEMS_PER_PAGE)
      );
      
      // If not first page, start after the last document
      if (page > 1 && lastVisible && !isRefresh) {
        mediaQuery = query(
          collection(db, 'imageAssets'),
          orderBy('uploadedAt', 'desc'),
          startAfter(lastVisible),
          limit(ITEMS_PER_PAGE)
        );
      }
      
      const snapshot = await getDocs(mediaQuery);
      const mediaList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Get the last visible document for pagination
      const lastDoc = snapshot.docs[snapshot.docs.length - 1];
      setLastVisible(lastDoc);
      
      // Check if there are more pages
      if (snapshot.docs.length === ITEMS_PER_PAGE) {
        // Check if there's a next page by trying to fetch one more
        const nextQuery = query(
          collection(db, 'imageAssets'),
          orderBy('uploadedAt', 'desc'),
          startAfter(lastDoc),
          limit(1)
        );
        const nextSnapshot = await getDocs(nextQuery);
        setHasNextPage(!nextSnapshot.empty);
      } else {
        setHasNextPage(false);
      }
      
      if (page === 1 || isRefresh) {
        setMedia(mediaList);
      } else {
        setMedia(prevMedia => [...prevMedia, ...mediaList]);
      }
      
      // Update total count (approximate)
      setTotalCount(prev => page === 1 ? mediaList.length : prev + mediaList.length);
      
    } catch (err) {
      console.error('Error fetching media:', err);
      setError('Failed to load media files');
    } finally {
      setLoading(false);
    }
  }, [lastVisible]);

  // Initial load
  useEffect(() => {
    fetchMedia(1, true);
  }, []);

  // IMPROVED: Pagination handlers
  const handleLoadMore = useCallback(() => {
    if (!loading && hasNextPage) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchMedia(nextPage);
    }
  }, [currentPage, hasNextPage, loading, fetchMedia]);

  const handleRefresh = useCallback(() => {
    setCurrentPage(1);
    setLastVisible(null);
    setHasNextPage(false);
    fetchMedia(1, true);
    setMessage({ type: 'success', text: 'Media library refreshed' });
    setTimeout(() => setMessage({ type: '', text: '' }), 3000);
  }, [fetchMedia]);

  // OPTIMIZED: Search handler with debounce
  const handleSearch = useCallback((value) => {
    setSearchTerm(value);
  }, []);

  // OPTIMIZED: Category filter handler
  const handleCategoryFilter = useCallback((category) => {
    setSelectedCategory(category);
  }, []);

  // Media selection handler
  const handleMediaClick = useCallback((mediaItem) => {
    if (onSelectMedia) {
      onSelectMedia(mediaItem);
    }
  }, [onSelectMedia]);

  // Delete handler
  const handleDelete = useCallback(async (mediaId, event) => {
    event.stopPropagation();
    
    if (!window.confirm('Are you sure you want to delete this image?')) {
      return;
    }
    
    setIsDeleting(mediaId);
    try {
      await MediaService.deleteMediaAsset(mediaId);
      setMedia(prevMedia => prevMedia.filter(item => item.id !== mediaId));
      setMessage({ type: 'success', text: 'Image deleted successfully' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error deleting media:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to delete image' });
      setTimeout(() => setMessage({ type: '', text: '' }), 5000);
    } finally {
      setIsDeleting(null);
    }
  }, []);

  // Preview handlers
  const openPreview = useCallback((mediaItem, event) => {
    event.stopPropagation();
    setShowPreview(mediaItem);
  }, []);

  const closePreview = useCallback(() => {
    setShowPreview(null);
  }, []);

  // Format file size
  const formatFileSize = useCallback((bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }, []);

  // Render media item
  const renderMediaItem = useCallback((item) => (
    <div
      key={item.id}
      className={`media-item ${selectedMediaId === item.id ? 'selected' : ''}`}
      onClick={() => handleMediaClick(item)}
    >
      <div className="media-item-header">
        <div className="media-name" title={item.filename}>
          {item.filename}
        </div>
        <button
          className="delete-btn"
          onClick={(e) => handleDelete(item.id, e)}
          disabled={isDeleting === item.id}
          title="Delete image"
        >
          {isDeleting === item.id ? '⏳' : '🗑️'}
        </button>
      </div>
      
      <div className="media-preview">
        {item.usedInUnits && item.usedInUnits.length > 0 && (
          <div className="usage-badge">
            Used in {item.usedInUnits.length} unit{item.usedInUnits.length !== 1 ? 's' : ''}
          </div>
        )}
        
        <img
          src={item.url}
          alt={item.altText || item.filename}
          className="media-preview-image"
          loading="lazy"
        />
        
        <button
          className="preview-btn"
          onClick={(e) => openPreview(item, e)}
          title="Preview image"
        >
          👁️
        </button>
      </div>
      
      <div className="media-details">
        <span>{formatFileSize(item.fileSize || 0)}</span>
        <span>{item.dimensions || 'Unknown'}</span>
      </div>
      
      <div className="media-meta">
        <span className="upload-date">
          {item.uploadedAt ? new Date(item.uploadedAt.seconds * 1000).toLocaleDateString() : 'Unknown'}
        </span>
        <span className="category-badge">{item.category || 'No category'}</span>
      </div>
    </div>
  ), [selectedMediaId, handleMediaClick, handleDelete, isDeleting, openPreview, formatFileSize]);

  return (
    <div className="media-browser">
      <div className="browser-header">
        <h3>Browse Images ({totalCount}+ items)</h3>
        
        <div className="browser-actions">
          <button onClick={handleRefresh} className="refresh-btn" disabled={loading}>
            {loading ? '⏳' : '🔄'} Refresh
          </button>
        </div>
      </div>

      {message.text && (
        <div className={`message-banner ${message.type}`}>
          {message.text}
        </div>
      )}

      {showSearchBar && (
        <div className="search-container">
          <input
            type="text"
            placeholder="Search images by name, tags, description..."
            value={searchTerm}
            onChange={(e) => handleSearch(e.target.value)}
            className="search-input"
          />
        </div>
      )}

      {showCategoryFilter && (
        <div className="filter-controls">
          <select
            value={selectedCategory}
            onChange={(e) => handleCategoryFilter(e.target.value)}
            className="filter-select"
          >
            <option value="">All Categories</option>
            {categories.map(category => (
              <option key={category} value={category}>
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </option>
            ))}
          </select>
        </div>
      )}

      {error && (
        <div className="error-container">
          {error}
        </div>
      )}

      {loading && media.length === 0 && (
        <div className="loading-container">
          Loading media files...
        </div>
      )}

      {!loading && filteredMedia.length === 0 && (
        <div className="no-items-message">
          {media.length === 0 ? 'No images found. Upload some images to get started.' : 'No images match your search criteria.'}
        </div>
      )}

      {filteredMedia.length > 0 && (
        <>
          <div className="media-grid">
            {filteredMedia.map(renderMediaItem)}
          </div>
          
          {/* PAGINATION CONTROLS */}
          {hasNextPage && !loading && (
            <div className="pagination-controls" style={{
              textAlign: 'center',
              marginTop: '20px',
              padding: '20px'
            }}>
              <button
                onClick={handleLoadMore}
                className="load-more-btn"
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#2196f3',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                Load More Images ({ITEMS_PER_PAGE} more)
              </button>
              <div style={{
                marginTop: '10px',
                fontSize: '12px',
                color: '#666'
              }}>
                Showing {filteredMedia.length} of {totalCount}+ images
              </div>
            </div>
          )}
          
          {loading && media.length > 0 && (
            <div style={{
              textAlign: 'center',
              padding: '20px',
              color: '#666'
            }}>
              Loading more images...
            </div>
          )}
        </>
      )}

      {/* Preview Modal */}
      {showPreview && (
        <div className="preview-modal" onClick={closePreview}>
          <div className="preview-content" onClick={(e) => e.stopPropagation()}>
            <button className="close-button" onClick={closePreview}>×</button>
            <h3>{showPreview.filename}</h3>
            <div className="preview-media">
              <img src={showPreview.url} alt={showPreview.altText || showPreview.filename} />
            </div>
            <div className="preview-details">
              <p><strong>Size:</strong> {formatFileSize(showPreview.fileSize || 0)}</p>
              <p><strong>Dimensions:</strong> {showPreview.dimensions || 'Unknown'}</p>
              <p><strong>Category:</strong> {showPreview.category || 'No category'}</p>
              {showPreview.altText && <p><strong>Alt Text:</strong> {showPreview.altText}</p>}
              {showPreview.description && <p><strong>Description:</strong> {showPreview.description}</p>}
              {showPreview.tags && showPreview.tags.length > 0 && (
                <p><strong>Tags:</strong> {showPreview.tags.join(', ')}</p>
              )}
              <p><strong>Uploaded:</strong> {showPreview.uploadedAt ? new Date(showPreview.uploadedAt.seconds * 1000).toLocaleString() : 'Unknown'}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(MediaBrowser);